import { Popover } from "radix-ui";
import { useState } from "react";
import AutoCompleteBruger from "@/components/autocomplete/AutoCompleteBruger";
import Button from "@/components/bricks/button/Button";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon";
import Spinner from "@/components/spinner/Spinner";
import {
  useAssignReviewThread,
  useAssignReviewThreadToSelf,
} from "@/hooks/cle-hooks";
import { showCleApiErrorToast } from "@/lib/showCleApiErrorToast";
import { useToaster } from "rsuite";

interface ReviewThreadAssignPopoverProps {
  threadId: string;
  isAssignedToMe?: boolean;
  disabled?: boolean;
}

export const ReviewThreadAssignPopover = ({
  threadId,
  isAssignedToMe,
  disabled,
}: ReviewThreadAssignPopoverProps) => {
  const [open, setOpen] = useState(false);
  const toaster = useToaster();
  const { mutate: assign, isPending: isAssigning } = useAssignReviewThread();
  const { mutate: assignToSelf, isPending: isAssigningSelf } =
    useAssignReviewThreadToSelf();

  const isPending = isAssigning || isAssigningSelf;
  const isDisabled = disabled || isPending;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      document.body.style.pointerEvents = "auto";
    }
    setOpen(nextOpen);
  };

  const handleAdminSelect = (admin: { email: string; navn: string }) => {
    assign(
      { threadId, assigneeEmail: admin.email, adminDisplayName: admin.navn },
      {
        onSuccess: () => setOpen(false),
        onError: (err) => showCleApiErrorToast(toaster, err),
      },
    );
  };

  const handleAssignSelf = () => {
    assignToSelf(threadId, {
      onSuccess: () => setOpen(false),
      onError: (err) => showCleApiErrorToast(toaster, err),
    });
  };

  return (
    <Popover.Root modal={false} open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <Button
          id="review-thread-assign"
          color="-action"
          type="button"
          disabled={isDisabled}
          className="c-popover-trigger"
        >
          {isPending ? <Spinner size={14} /> : null}
          Tildel
          <ChevronDownIcon width={20} />
        </Button>
      </Popover.Trigger>
      <Popover.Content
        align="end"
        className="c-popover-content c-review-thread-top-panel__assign-popover"
      >
        {open ? (
          <div className="w-100">
            <AutoCompleteBruger
              key="review-thread-assign-admin-search"
              clearAfterSelection
              placeholder="Søg på navn eller e-mail ..."
              onSelectedItemChange={handleAdminSelect}
            />
          </div>
        ) : null}
        <p className="text-muted text-center mx-auto small mb-0">Eller</p>
        <Button
          id="review-thread-assign-self"
          color="primary"
          type="button"
          disabled={isPending}
          fullWidth
          onClick={handleAssignSelf}
        >
          {isAssignedToMe ? "Fjern fra mig" : "Tildel til mig"}
        </Button>
      </Popover.Content>
    </Popover.Root>
  );
};
